import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../components/Icon";
import { useAuth } from "../auth";
import { apiGet, apiSend } from "../api";
import { FSA_ALLERGENS } from "../constants";
import { exportAllergenMatrix } from "../export";
import { formatYmd } from "../dateutil";
import { C, R, S } from "../theme";

const PURPLE = "#8b5cf6";
const todayYmd = () => new Date().toLocaleDateString("en-CA");
const emptyAllergens = () => Object.fromEntries(FSA_ALLERGENS.map((a) => [a.key, false]));

export default function AllergenMatrix() {
  const { id } = useParams();
  const { user, isManager } = useAuth();
  const nav = useNavigate();
  const [unit, setUnit] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [reviewDate, setReviewDate] = useState(todayYmd());
  const [reviewedBy, setReviewedBy] = useState("");
  const [newDish, setNewDish] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet(`/attractions/${id}`).then(setUnit).catch(() => {});
    apiGet(`/attractions/${id}/allergen-matrix`).then((mx) => {
      setDishes((mx.dishes || []).map((d) => ({ ...d, allergens: { ...emptyAllergens(), ...(d.allergens || {}) } })));
      if (mx.last_review_date) setReviewDate(mx.last_review_date);
      setReviewedBy(mx.reviewed_by || user?.display_name || "");
    }).catch(() => {}).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addDish = () => {
    const nm = newDish.trim();
    if (!nm) return;
    setDishes((p) => [...p, { id: `tmp-${Date.now()}`, name: nm, allergens: emptyAllergens() }]);
    setNewDish("");
  };
  const removeDish = (idx) => setDishes((p) => {
    const d = p[idx];
    if (d && (d.name || "").trim() && typeof window !== "undefined" && !window.confirm(`Delete "${d.name.trim()}" from the allergen matrix?`)) return p;
    return p.filter((_, i) => i !== idx);
  });
  const renameDish = (idx, name) => setDishes((p) => p.map((d, i) => (i === idx ? { ...d, name } : d)));
  const toggle = (idx, key) => setDishes((p) => p.map((d, i) => (i === idx ? { ...d, allergens: { ...d.allergens, [key]: !d.allergens[key] } } : d)));

  const save = async () => {
    setBusy(true); setMsg("");
    try {
      const payload = {
        dishes: dishes.filter((d) => (d.name || "").trim()).map((d) => ({ id: String(d.id || "").startsWith("tmp-") ? null : d.id, name: d.name.trim(), allergens: d.allergens })),
        last_review_date: reviewDate || null,
        reviewed_by: reviewedBy.trim() || null,
      };
      const saved = await apiSend(`/attractions/${id}/allergen-matrix`, "PUT", payload);
      setDishes((saved.dishes || []).map((d) => ({ ...d, allergens: { ...emptyAllergens(), ...(d.allergens || {}) } })));
      setMsg("✅ Allergen matrix saved");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setMsg("❌ " + (e?.message || "Could not save")); }
    finally { setBusy(false); }
  };

  const downloadPdf = () => {
    exportAllergenMatrix(user?.company_name || "", unit?.name || "Catering Unit", {
      dishes: dishes.filter((d) => (d.name || "").trim()).map((d) => ({ name: d.name.trim(), allergens: d.allergens })),
      last_review_date: reviewDate || null,
      reviewed_by: reviewedBy.trim() || null,
    });
  };

  const activeCount = (d) => FSA_ALLERGENS.filter((a) => d.allergens[a.key]).length;

  return (
    <div data-testid="allergen-matrix-page" className="fade-up">
      <div style={{ display: "flex", alignItems: "center", gap: S.sm, padding: S.lg }}>
        <button data-testid="am-back" className="pressable" onClick={() => nav(-1)}><Icon name="chevron-back" size={28} color={C.text} /></button>
        <div style={{ flex: 1 }}>
          <div className="display" style={{ color: C.text, fontSize: 20, fontWeight: 900 }}>Allergen Matrix</div>
          {unit && <div style={{ color: C.muted, fontSize: 13 }}>{unit.name} · FSA 14 allergens</div>}
        </div>
        <div style={{ width: 40, height: 40, borderRadius: 11, background: PURPLE, display: "flex", alignItems: "center", justifyContent: "center" }}><Icon name="grid" size={22} color={C.black} /></div>
      </div>

      <div style={{ padding: `0 ${S.lg}px ${S.xxl}px` }}>
        {/* Review metadata */}
        <div style={{ background: C.card, borderRadius: R.md, border: `1px solid ${C.border}`, padding: S.md, marginBottom: S.md }}>
          <div style={{ color: C.sub, fontSize: 13, fontWeight: 800, marginBottom: S.sm, textTransform: "uppercase" }}>Audit & Review</div>
          <div style={{ display: "flex", gap: S.sm, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 140px" }}>
              <div style={{ color: C.muted, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Last review date</div>
              <input data-testid="am-review-date" type="date" value={reviewDate} disabled={!isManager} onChange={(e) => setReviewDate(e.target.value)}
                style={{ width: "100%", background: C.card2, border: `1px solid ${C.borderStrong}`, borderRadius: R.md, color: C.text, fontSize: 15, minHeight: 48, padding: `0 ${S.md}px`, outline: "none", boxSizing: "border-box" }} />
            </div>
            <div style={{ flex: "1 1 140px" }}>
              <div style={{ color: C.muted, fontSize: 12, fontWeight: 700, marginBottom: 4 }}>Reviewed by</div>
              <input data-testid="am-reviewed-by" type="text" value={reviewedBy} disabled={!isManager} onChange={(e) => setReviewedBy(e.target.value)} placeholder="Staff / owner name"
                style={{ width: "100%", background: C.card2, border: `1px solid ${C.borderStrong}`, borderRadius: R.md, color: C.text, fontSize: 15, minHeight: 48, padding: `0 ${S.md}px`, outline: "none", boxSizing: "border-box" }} />
            </div>
          </div>
        </div>

        {/* Add dish */}
        {isManager && (
          <div style={{ display: "flex", gap: S.sm, marginBottom: S.md }}>
            <input data-testid="am-new-dish-input" type="text" value={newDish} onChange={(e) => setNewDish(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addDish(); }} placeholder="Add a dish / menu item (e.g. Bacon Cheeseburger)"
              style={{ flex: 1, background: C.card2, border: `1px solid ${C.borderStrong}`, borderRadius: R.md, color: C.text, fontSize: 15, minHeight: 50, padding: `0 ${S.md}px`, outline: "none", boxSizing: "border-box" }} />
            <button data-testid="am-add-dish" className="pressable" onClick={addDish} style={{ background: PURPLE, color: C.black, border: "none", borderRadius: R.md, minHeight: 50, padding: `0 ${S.lg}px`, fontWeight: 900, display: "flex", alignItems: "center", gap: 6 }}><Icon name="add" size={18} color={C.black} /> Add</button>
          </div>
        )}

        {loading ? (
          <div style={{ color: C.amber, textAlign: "center", padding: S.xl }}>Loading…</div>
        ) : dishes.length === 0 ? (
          <div style={{ textAlign: "center", padding: `${S.xl}px 0`, color: C.muted }}>
            <Icon name="restaurant" size={44} color={C.muted} />
            <div style={{ color: C.text, fontWeight: 700, marginTop: S.sm }}>No dishes yet</div>
            <div style={{ fontSize: 13, marginTop: 4 }}>{isManager ? "Add your menu items above, then tick the allergens each contains." : "No menu items have been added yet."}</div>
          </div>
        ) : dishes.map((d, idx) => (
          <div key={d.id || idx} data-testid={`am-dish-${idx}`} style={{ background: C.card, borderRadius: R.md, border: `1px solid ${C.border}`, padding: S.md, marginBottom: S.sm }}>
            <div style={{ display: "flex", alignItems: "center", gap: S.sm, marginBottom: S.sm }}>
              {isManager ? (
                <input data-testid={`am-dish-name-${idx}`} type="text" value={d.name} onChange={(e) => renameDish(idx, e.target.value)}
                  style={{ flex: 1, background: "transparent", border: "none", borderBottom: `1px solid ${C.borderStrong}`, color: C.text, fontSize: 16, fontWeight: 800, minHeight: 36, padding: "0 2px", outline: "none" }} />
              ) : (
                <div style={{ flex: 1, color: C.text, fontSize: 16, fontWeight: 800 }}>{d.name}</div>
              )}
              <span style={{ color: activeCount(d) ? C.amber : C.muted, fontSize: 12, fontWeight: 800 }}>{activeCount(d)}/14</span>
              {isManager && <button data-testid={`am-dish-delete-${idx}`} className="pressable" onClick={() => removeDish(idx)} style={{ background: "transparent", border: "none", cursor: "pointer" }}><Icon name="trash" size={20} color={C.red} /></button>}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {FSA_ALLERGENS.map((a) => {
                const on = !!d.allergens[a.key];
                return (
                  <button key={a.key} data-testid={`am-chip-${idx}-${a.key}`} disabled={!isManager} onClick={() => isManager && toggle(idx, a.key)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, background: on ? C.amber : C.card2, color: on ? C.black : C.sub, border: `1px solid ${on ? C.amber : C.borderStrong}`, borderRadius: R.pill, padding: "6px 10px", fontSize: 12, fontWeight: 700, cursor: isManager ? "pointer" : "default" }}>
                    {on && <Icon name="checkmark" size={12} color={C.black} />} {a.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {!!msg && <div data-testid="am-status" style={{ color: msg.startsWith("✅") ? C.green : C.red, fontWeight: 800, fontSize: 14, margin: `${S.md}px 0` }}>{msg}</div>}

        <div style={{ display: "flex", gap: S.sm, marginTop: S.lg }}>
          {isManager && (
            <button data-testid="am-save" className="pressable" disabled={busy} onClick={save} style={{ flex: 1, background: C.amber, color: C.black, border: "none", borderRadius: R.pill, minHeight: 54, fontWeight: 900, fontSize: 16, opacity: busy ? 0.7 : 1 }}>{busy ? "Saving…" : "Save Matrix"}</button>
          )}
          <button data-testid="am-download-pdf" className="pressable" onClick={downloadPdf} style={{ flex: 1, background: C.card2, color: C.amber, border: `1px solid ${C.borderStrong}`, borderRadius: R.pill, minHeight: 54, fontWeight: 900, fontSize: 15, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}><Icon name="download" size={20} color={C.amber} /> Download PDF</button>
        </div>
        <div style={{ color: C.muted, fontSize: 12, marginTop: S.md, lineHeight: 1.5 }}>The saved matrix is printable for window display and is auto-included in this unit's EHO Audit Pack (Reports → Food Safety (EHO)).</div>
      </div>
    </div>
  );
}
